/**
 * Painterly cloud layers (v0), evaluated inside the sky-dome fragment shader. Needs ATMOSPHERE_GLSL (raySphere,
 * lightTransmittance, Rg) and the dome's uniforms. Lattice coordinates wrap at CLOUD_NOISE_PERIOD (256) so the
 * CPU-wrapped drift offsets (cloudOffsets) are seamless.
 */
export const CLOUD_GLSL = /* glsl */ `
uniform vec4 uCloudA[3];      // threshold, softness, scaleM, altitudeM
uniform vec2 uCloudOffset[3]; // drift in noise units, already wrapped
uniform float uCloudDetail;   // octaves: 3 (low) … 5 (high)

float cHash(vec2 p) {
  p = mod(p, 256.0);
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

float cNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(cHash(i), cHash(i + vec2(1.0, 0.0)), u.x), mix(cHash(i + vec2(0.0, 1.0)), cHash(i + vec2(1.0, 1.0)), u.x), u.y);
}

float cFbm(vec2 p) {
  float s = 0.0;
  float a = 0.5;
  float n = 0.0;
  for (int o = 0; o < 5; o++) {
    if (float(o) >= uCloudDetail) break;
    s += a * cNoise(p);
    n += a;
    p = p * 2.03 + vec2(17.1, 9.3);
    a *= 0.5;
  }
  return s / n;
}

float cHG(float c, float g) {
  float g2 = g * g;
  return (1.0 - g2) / (4.0 * PI * pow(max(1e-4, 1.0 + g2 - 2.0 * g * c), 1.5));
}

/**
 * Composites the three layers over col (far to near: cirrus, alto, cumulus). ambient = sky radiance used for the
 * shaded side; sunI / moonI = light illuminances; sunDir / moonDir in the planet frame (y up).
 */
vec3 applyClouds(vec3 col, vec3 d, float camR, vec3 sunDir, float sunI, vec3 moonDir, float moonI, vec3 ambient, sampler2D transLut) {
  if (d.y <= 0.0) return col;
  vec3 o = vec3(0.0, camR, 0.0);
  float horizonFade = smoothstep(0.015, 0.12, d.y);
  for (int k = 2; k >= 0; k--) {
    vec4 a = uCloudA[k];
    float t = raySphere(o, d, Rg + a.w);
    if (t <= 0.0) continue;
    vec3 p = o + d * t;
    vec2 q = p.xz / a.z + uCloudOffset[k];
    vec2 warp = vec2(cFbm(q * 0.5 + 3.1), cFbm(q * 0.5 + 7.7)) - 0.5;
    float n = cFbm(q + warp * 0.9);
    float dens = smoothstep(a.x, a.x + a.y, n);
    // painterly: pull density toward 5 soft bands
    float band = floor(dens * 5.0 + 0.5) / 5.0;
    dens = mix(dens, band, 0.45) * horizonFade * (k == 2 ? 0.55 : 1.0);
    if (dens <= 0.001) continue;
    float thin = 1.0 - dens;
    vec3 sunT = lightTransmittance(transLut, p, sunDir) * sunI;
    float cs = dot(d, sunDir);
    float phase = 0.7 * cHG(cs, 0.6) + 0.3 * cHG(cs, -0.2);
    vec3 lit = sunT * (0.05 + phase * (0.35 + 1.4 * thin));
    vec3 moonT = lightTransmittance(transLut, p, moonDir) * moonI;
    lit += moonT * (0.06 + 0.5 * cHG(dot(d, moonDir), 0.5));
    vec3 cloud = lit + ambient * (0.55 + 0.25 * thin);
    col = mix(col, cloud, clamp(dens, 0.0, 1.0));
  }
  return col;
}
`;
